import type { BusinessStatus, DashboardCountyRow, DashboardModel } from "./dashboard-model.js";
import { escapeHtml, labelize } from "./dashboard-html.js";

export function defaultFutureDatetimeLocal(daysFromNow: number): string {
  return new Date(Date.now() + daysFromNow * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
}

export function datetimeLocalToIso(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

export function gateState(model: DashboardModel, id: string): string {
  return model.gates.find((gate) => gate.id === id)?.state ?? "not_ready";
}

export function filterRows(rows: DashboardCountyRow[], state: { county: string; status: string; action: string; sort: string }): DashboardCountyRow[] {
  const filtered = rows.filter((row) => {
    if (state.county && !row.county.toLowerCase().includes(state.county)) return false;
    if (state.status && row.businessStatus !== state.status) return false;
    if (state.action && row.nextAction !== state.action) return false;
    return true;
  });

  return filtered.sort((a, b) => {
    if (state.sort === "expected") return b.expectedFilings - a.expectedFilings;
    if (state.sort === "candidates") return (b.candidateCount ?? -1) - (a.candidateCount ?? -1);
    if (state.sort === "pdfs") return b.pdfsDownloaded - a.pdfsDownloaded;
    if (state.sort === "missing") return (b.missingPdfCount ?? -1) - (a.missingPdfCount ?? -1);
    return a.rank - b.rank;
  });
}

export function businessStatusOptions(): string {
  const statuses: BusinessStatus[] = ["pdf_captured", "partial", "missing_pdf_evidence", "blocked", "zero_records", "needs_work"];
  return statuses.map((status) => `<option value="${status}">${escapeHtml(labelize(status))}</option>`).join("");
}

export function lastDayOfMonth(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return "";
  const day = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, "0")}`;
}

export function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing dashboard element ${selector}`);
  return element;
}

export function cssEscape(value: string): string {
  if (globalThis.CSS?.escape) return globalThis.CSS.escape(value);
  return value.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
}
